import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"
import "pdfjs-dist/build/pdf.worker.mjs"

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = "pdfjs-dist/build/pdf.worker.mjs"

export default class extends Controller {
  static targets = [ "canvas", "previous", "next", "currentPage", "numPages" ]
  static values = {
    url: String,
    initialPage: { type: Number, default: 1 }
  }

  async connect() {
    try {
      // Load the PDF document
      this.pdf = await pdfjsLib.getDocument(this.urlValue).promise

      // Set up initial state
      this.currentPage = this.initialPageValue
      this.totalPages = this.pdf.numPages
      this.numPagesTarget.textContent = this.totalPages

      // Render initial page
      await this.renderPage(this.currentPage)

      // Enable/disable navigation buttons
      this.updateNavigationButtons()
    } catch (error) {
      console.error("Error loading PDF:", error)
    }
  }

  async renderPage(pageNumber) {
    try {
      // Get the page
      const page = await this.pdf.getPage(pageNumber)

      // Get the canvas context
      const canvas = this.canvasTarget
      const context = canvas.getContext('2d')

      // Calculate scale to fit canvas
      const viewport = page.getViewport({ scale: 1.5 })
      canvas.height = viewport.height
      canvas.width = viewport.width

      // Render page on canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      }
      await page.render(renderContext)

      // Update page number display
      if (this.hasCurrentPageTarget) {
        this.currentPageTarget.value = pageNumber
      }
    } catch (error) {
      console.error("Error rendering page:", error)
    }
  }

  async next() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++
      await this.renderPage(this.currentPage)
      this.updateNavigationButtons()
    }
  }

  async previous() {
    if (this.currentPage > 1) {
      this.currentPage--
      await this.renderPage(this.currentPage)
      this.updateNavigationButtons()
    }
  }

  async goTo() {
    let page = Number(this.currentPageTarget.value.trim())

    if (!Number.isInteger(page) || page <= 0 || page > this.totalPages) {
      this.currentPageTarget.value = this.currentPage
      return
    }

    this.currentPage = page
    await this.renderPage(this.currentPage)
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    // Disable previous button on first page
    if (this.hasPreviousTarget) {
      this.previousTarget.disabled = this.currentPage === 1
    }

    // Disable next button on last page
    if (this.hasNextTarget) {
      this.nextTarget.disabled = this.currentPage === this.totalPages
    }
  }
}
